// Scrolling helpers: page and element scrolling (DwsPageBaseComponent, DwsAnchorLink, DwsDataGrid, DwsSelect,
// DwsNavMenu), the base page's header offset and the document title.

import { publish } from "../shared/publish";

function SetDocumentTitle(title: string): void {
    document.title = title;
}

function ScrollToTop(elementId: string): void {
    let element = document.getElementById(elementId);

    if (!element) return;

    element.scrollTo({
        top: 0,
        behavior: 'smooth'
    });
}

// DwsPageBaseComponent: keeps --dws-basepage-header-offset on the page frame at the height of the page's breadcrumb bar,
// so the top anchor groups sit below the bar however many lines it wraps to (one on a desktop, three on a phone). The
// frame carries no Blazor style attribute, so a render of the page does not wipe the value out again.
function DwsBasePageObserveHeader(pageId: string): boolean {
    let page = document.getElementById(pageId);

    if (!page || typeof ResizeObserver !== 'function') {
        return false;
    }

    DwsBasePageUnobserveHeader(pageId);

    let frame = page.parentElement;
    const header = page.querySelector<HTMLElement>(':scope > .dws-navbar-sticky-top');

    if (!frame || !header) {
        return false;
    }

    page._dwsHeaderObserver = new ResizeObserver(function () {
        frame.style.setProperty('--dws-basepage-header-offset', header.offsetHeight + 'px');
    });

    page._dwsHeaderObserver.observe(header);
    return true;
}

function DwsBasePageUnobserveHeader(pageId: string): void {
    let page = document.getElementById(pageId);

    if (page && page._dwsHeaderObserver) {
        page._dwsHeaderObserver.disconnect();
        page._dwsHeaderObserver = null;
    }
}

// Scrolls a scroll container (e.g. a virtualized DwsDataGrid body) back to its top; instant unless smooth is set.
function ScrollElementToTop(elementId: string, smooth?: boolean): void {
    let element = document.getElementById(elementId);

    if (!element) {
        return;
    }

    if (smooth && typeof element.scrollTo === 'function') {
        element.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
        element.scrollTop = 0;
    }
}

function ScrollToElementID(elementId: string, addClass: string): void {
    let element = document.getElementById(elementId);

    if (!element) {
        return;
    }

    element.classList.add(addClass);
    element.scrollIntoView();
}

// offset (optional, DwsAnchorLink.ScrollOffset): pixels kept free above the target for this one scroll - a sticky
// header. Set as scroll-margin-top while scrollIntoView reads the target's position, then put back.
function ScrollIntoView(elementId: string, offset?: number | null): void {
    let elem = document.getElementById(elementId);

    if (elem) {
        if (typeof offset === 'number' && offset > 0) {
            let previousMargin = elem.style.scrollMarginTop;

            elem.style.scrollMarginTop = offset + 'px';
            elem.scrollIntoView();
            elem.style.scrollMarginTop = previousMargin;
        } else {
            elem.scrollIntoView();
        }

        // Show the target in the address bar without a new history entry. Assigning location.hash pushed an entry
        // Blazor's router does not know (null state), so its next location-changing check computed a wrong history
        // delta: Back jumped several pages or the page reloaded. replaceState keeps Blazor's own state object.
        try {
            // The full path, not a bare '#id': the app's <base href="/"> would resolve that to the home page's URL.
            history.replaceState(history.state, '', location.pathname + location.search + '#' + encodeURIComponent(elementId));
        } catch (e) {
            // A sandboxed document may refuse it; the scroll already happened.
        }
    }
}

function ScrollToListElement(elementId: string): void {
    let element = document.getElementById(elementId);

    if (element) {
        let currentScrollY = window.scrollY || window.pageYOffset;
        window.scrollTo({ top: currentScrollY + 1, behavior: 'instant' });
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
}

// Centers an element in its nearest scrolling ancestor without scrollIntoView: Chromium moves the sequential focus
// navigation starting point to an element scrolled into view, so after the navigation menu revealed its active link on
// page load, the first Tab skipped the page's skip link and landed inside the menu.
// The menu's groups expand (and animate their height) right before this runs, so the element moves for a while: keep
// it centered over the next frames, for about half a second, unless something else scrolls the scroller meanwhile.
function DwsScrollElementIntoScroller(elementId: string): void {
    let frames = 0;
    let lastScroller = null;
    let lastTop = -1;

    function step() {
        let element = document.getElementById(elementId);

        if (lastScroller && lastScroller.scrollTop !== lastTop) {
            // The user (or the page) scrolled the menu: leave it there.
            return;
        }

        let scroller = element ? DwsScrollElementIntoScrollerOnce(element) : null;

        if (scroller) {
            lastScroller = scroller;
            lastTop = scroller.scrollTop;
        }

        if (++frames < 30) {
            requestAnimationFrame(step);
        }
    }

    step();
}

// Returns the scroller it scrolled, or null when the element is hidden or has no scrolling ancestor (yet).
function DwsScrollElementIntoScrollerOnce(element: HTMLElement): HTMLElement | null {
    // Still inside a collapsed group.
    if (element.getClientRects().length === 0) {
        return null;
    }

    let scroller = element.parentElement;

    while (scroller && scroller !== document.body) {
        let overflowY = getComputedStyle(scroller).overflowY;

        if ((overflowY === 'auto' || overflowY === 'scroll') && scroller.scrollHeight > scroller.clientHeight) {
            break;
        }

        scroller = scroller.parentElement;
    }

    if (!scroller || scroller === document.body) {
        return null;
    }

    let elementRect = element.getBoundingClientRect();
    let scrollerRect = scroller.getBoundingClientRect();
    let offset = elementRect.top - scrollerRect.top - (scroller.clientHeight - elementRect.height) / 2;

    scroller.scrollTop = Math.max(0, scroller.scrollTop + offset);

    return scroller;
}

publish("SetDocumentTitle", SetDocumentTitle);
publish("ScrollToTop", ScrollToTop);
publish("DwsBasePageObserveHeader", DwsBasePageObserveHeader);
publish("DwsBasePageUnobserveHeader", DwsBasePageUnobserveHeader);
publish("ScrollElementToTop", ScrollElementToTop);
publish("ScrollToElementID", ScrollToElementID);
publish("ScrollIntoView", ScrollIntoView);
publish("ScrollToListElement", ScrollToListElement);
publish("DwsScrollElementIntoScroller", DwsScrollElementIntoScroller);
